/**
 * JourneyPage — The Transformation Journey (Jigyasa → Siddhi).
 * Pixel-faithful reproduction of the provided source-of-truth Figma reference.
 *
 * Architecture:
 *   - Sequential 5-stage wizard. One stage rendered at a time. Back/Next nav.
 *   - Stage 1 (Jigyasa)  — book intro pulled from existing /api/mycraftz/showcase
 *   - Stage 2 (Bodha)    — Key Insights list + "IF YOU REMEMBER ONLY ONE THING…"
 *   - Stage 3 (Sankalpa) — Choose one idea (radio cards, amber glow on selected)
 *   - Stage 4 (Sadhana)  — CraftPrint™ blueprint (Sutra/Trigger/Action/...)
 *   - Stage 5 (Siddhi)   — Progress dial, week grid, reflection, milestones
 *
 * Auth:
 *   - Stages 1-3 are public (browse the journey).
 *   - Stage 4 → "START MY 30-DAY JOURNEY" requires login (POST /install).
 *   - Stage 5 shows live install data when authenticated + installed.
 */
import { bookIdForSlug } from "../data/mycraftzShowcase";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, BookMarked, BrainCircuit, HandMetal, Flower2,
  Star, Clock, Calendar, Quote, ChevronRight, Bookmark, Mountain,
  Repeat, UserCircle, Leaf, Link2, Sun, Feather, Target, Gift,
  AlertTriangle, ScrollText, Pencil, Flag, Lock, Flame, Sparkles,
  BookOpen, Brain, Hand,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { bookExamples } from "../data/bookExamples";
import "../styles/JourneyPage.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom inline icon for SADHANA (seated meditator) — lucide has no match.
function MeditatorIcon({ size = 30, strokeWidth = 1.4 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"
      stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round">
      <circle cx="16" cy="9" r="3.2" />
      <path d="M11 14.5c1.5-1 3-1.4 5-1.4s3.5.4 5 1.4" />
      <path d="M9 20c2-3 4-3.6 7-3.6s5 .6 7 3.6" />
      <path d="M5 25c3-2.5 7-3 11-3s8 .5 11 3" />
      <path d="M16 14.5v2" opacity="0.4" />
    </svg>
  );
}

const STAGES = [
  { num: 1, key: "jigyasa", title: "JIGYASA", sub: "Curiosity",     verb: "I'M CURIOUS",   line: "I discover a book that sparks me.",  icon: BookMarked },
  { num: 2, key: "bodha",    title: "BODHA",    sub: "Understanding", verb: "I UNDERSTAND",  line: "I gain clarity from timeless insights.", icon: BrainCircuit },
  { num: 3, key: "sankalpa", title: "SANKALPA", sub: "Commitment",    verb: "I COMMIT",      line: "I choose one idea to transform my life.", icon: HandMetal },
  { num: 4, key: "sadhana",  title: "SADHANA",  sub: "Practice",      verb: "I PRACTICE",    line: "I show up daily and build momentum.",      icon: MeditatorIcon },
  { num: 5, key: "siddhi",   title: "SIDDHI",   sub: "Achievement",   verb: "I TRANSFORM",   line: "I achieve results and become my best self.", icon: Flower2 },
];

const INSIGHT_ICONS = {
  Mountain, Repeat, UserCircle, Leaf, Link: Link2, Heart: BookOpen,
  Sprout: Leaf, Shield: ScrollText, EyeOff: BookOpen, TrendingUp: ArrowRight,
  Compass: BookOpen, Target, Flame, Brain, Users: UserCircle,
  Anchor: Bookmark, Sunrise: Sun, Star, BookOpen, Clock, Wind: Repeat,
  Smartphone: BookOpen, Droplet: BookOpen,
};
function InsightIcon({ name, size = 22 }) {
  const C = INSIGHT_ICONS[name] || Sparkles;
  return <C size={size} strokeWidth={1.6} />;
}

/* ─────────────────────────────────────────────
   Top: numbered 5-step indicator with arrows.
   ───────────────────────────────────────────── */
function StageIndicator({ stage, onJump }) {
  return (
    <div className="jp-indicator" data-testid="jp-indicator">
      {STAGES.map((s, i) => (
        <React.Fragment key={s.key}>
          <button
            type="button"
            className={`jp-ind-step ${stage >= s.num ? "is-active" : ""} ${stage === s.num ? "is-current" : ""}`}
            onClick={() => onJump?.(s.num)}
            aria-label={`Go to ${s.title}`}
            data-testid={`jp-step-${s.key}`}
          >
            <span className="jp-ind-num">{s.num}</span>
            <span className="jp-ind-meta">
              <span className="jp-ind-title">{s.title}</span>
              <span className="jp-ind-sub">{s.sub}</span>
            </span>
          </button>
          {i < STAGES.length - 1 ? <ArrowRight size={16} className="jp-ind-arrow" /> : null}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Bottom: 5 circular stage icons + tagline.
   ───────────────────────────────────────────── */
function JourneyFooter() {
  return (
    <footer className="jp-footer" data-testid="jp-footer">
      <div className="jp-footer-row">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          return (
            <React.Fragment key={s.key}>
              <div className="jp-footer-step">
                <div className="jp-footer-ring" aria-hidden="true">
                  <Icon size={32} strokeWidth={1.4} />
                </div>
                <p className="jp-footer-name">{s.title}</p>
                <p className="jp-footer-verb">{s.verb}</p>
                <p className="jp-footer-line">{s.line}</p>
              </div>
              {i < STAGES.length - 1 ? <ArrowRight size={18} className="jp-footer-arrow" /> : null}
            </React.Fragment>
          );
        })}
      </div>
      <p className="jp-tagline">ONE BOOK. ONE IDEA. ONE TRANSFORMATION.</p>
    </footer>
  );
}

/* ─────────────────────────────────────────────
   Parchment card — torn-edge SVG mask wrapper.
   ───────────────────────────────────────────── */
function Parchment({ children, className = "", selected = false, dataTestid }) {
  return (
    <div
      className={`jp-parchment ${selected ? "is-selected" : ""} ${className}`}
      data-testid={dataTestid}
    >
      <div className="jp-parchment-inner">{children}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STAGE 1 — JIGYASA (Curiosity)
   ═══════════════════════════════════════════════════════════════ */
function Stage1Jigyasa({ book, bookData, onNext }) {
  const [tab, setTab] = useState("summary");
  if (!book) return null;
  // Look up the cover image from the iCraftz library by title.
  const libraryEntry = bookExamples.find((b) => b.title === book.title);
  const coverUrl = libraryEntry?.image || libraryEntry?.coverUrl;

  return (
    <Parchment dataTestid="jp-stage-1">
      <div className="jp-book-head">
        <div className="jp-book-cover">
          {coverUrl ? <img src={coverUrl} alt={book.title} /> : <div className="jp-book-cover-fallback">{book.title}</div>}
          <button type="button" className="jp-vault-btn jp-vault-btn-mini">Add to Vault</button>
        </div>
        <div className="jp-book-meta">
          <h2 className="jp-book-title">{book.title}</h2>
          <p className="jp-book-author">{book.author}</p>
          <p className="jp-book-rating">
            <Star size={14} fill="#DA9B4A" stroke="#DA9B4A" />
            <Star size={14} fill="#DA9B4A" stroke="#DA9B4A" />
            <Star size={14} fill="#DA9B4A" stroke="#DA9B4A" />
            <Star size={14} fill="#DA9B4A" stroke="#DA9B4A" />
            <Star size={14} fill="#DA9B4A" stroke="#DA9B4A" />
            <span> {book.rating} ({book.ratings_count})</span>
          </p>
          <p className="jp-book-category">{book.category}</p>
          <button type="button" className="jp-btn jp-btn-cream" data-testid="jp-add-to-vault">Add to Vault</button>
        </div>
      </div>

      <nav className="jp-tabs" role="tablist">
        {["summary", "concepts", "insights", "podcast"].map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`jp-tab ${tab === t ? "is-active" : ""}`}
            onClick={() => setTab(t)}
            data-testid={`jp-tab-${t}`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </nav>

      <section className="jp-section">
        <h3 className="jp-section-title">About the Book</h3>
        <p className="jp-section-body">{book.about}</p>
      </section>

      <dl className="jp-stat-grid">
        <div><dt><BookOpen size={13} /> Pages</dt><dd>{book.pages}</dd></div>
        <div><dt><Calendar size={13} /> Published</dt><dd>{book.published}</dd></div>
        <div><dt><Clock size={13} /> Read Time</dt><dd>{book.read_time}</dd></div>
      </dl>

      <section className="jp-section">
        <h3 className="jp-section-title">Core Premise</h3>
        <p className="jp-section-body">{book.core_premise}</p>
      </section>

      <blockquote className="jp-quote">
        <Quote size={14} className="jp-quote-mark" />
        <p>"{book.headline_quote}"</p>
        <cite>— {book.quote_author}</cite>
      </blockquote>

      <button
        type="button"
        className="jp-btn jp-btn-amber jp-btn-block"
        onClick={onNext}
        data-testid="jp-continue-reading"
      >
        Continue Reading <Bookmark size={14} />
      </button>
    </Parchment>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STAGE 2 — BODHA (Understanding)
   ═══════════════════════════════════════════════════════════════ */
function Stage2Bodha({ book, onNext }) {
  if (!book) return null;
  return (
    <Parchment dataTestid="jp-stage-2">
      <h2 className="jp-stage-eyebrow"><Sparkles size={14} /> KEY INSIGHTS</h2>
      <ul className="jp-insight-list">
        {(book.key_insights || []).map((ins) => (
          <li key={ins.id} className="jp-insight" data-testid={`jp-insight-${ins.id}`}>
            <span className="jp-insight-icon"><InsightIcon name={ins.icon} size={22} /></span>
            <div className="jp-insight-body">
              <h4 className="jp-insight-title">{ins.name}</h4>
              <p className="jp-insight-summary">{ins.summary}</p>
              <button type="button" className="jp-link">Read More <ChevronRight size={12} /></button>
            </div>
          </li>
        ))}
      </ul>

      <div className="jp-remember-block">
        <p className="jp-remember-eyebrow">IF YOU REMEMBER ONLY ONE THING…</p>
        <p className="jp-remember-text">{book.remember_one_thing}</p>
      </div>

      <button
        type="button"
        className="jp-btn jp-btn-amber jp-btn-block"
        onClick={onNext}
        data-testid="jp-install-one-idea"
      >
        INSTALL ONE IDEA <Lock size={13} />
      </button>
    </Parchment>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STAGE 3 — SANKALPA (Commitment)
   ═══════════════════════════════════════════════════════════════ */
function Stage3Sankalpa({ book, chosenId, setChosenId, onNext }) {
  if (!book) return null;
  const ideas = book.key_insights || [];
  return (
    <Parchment dataTestid="jp-stage-3">
      <h2 className="jp-stage-eyebrow">CHOOSE ONE IDEA TO INSTALL</h2>
      <p className="jp-stage-lead">
        Pick the idea that will create the biggest impact in your life right now.
      </p>

      <ul className="jp-idea-list" role="radiogroup">
        {ideas.map((ins) => {
          const selected = chosenId === ins.id;
          return (
            <li key={ins.id}>
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                className={`jp-idea ${selected ? "is-selected" : ""}`}
                onClick={() => setChosenId(ins.id)}
                data-testid={`jp-idea-${ins.id}`}
              >
                <span className="jp-idea-radio" aria-hidden="true">
                  {selected ? <Target size={14} /> : null}
                </span>
                <div className="jp-idea-body">
                  <h4 className="jp-idea-title">{ins.name}</h4>
                  <p className="jp-idea-summary">{ins.summary}</p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="jp-why-block">
        <p className="jp-why-eyebrow">WHY THIS IDEA?</p>
        <p className="jp-why-text">{book.craftprint?.why_this_idea}</p>
      </div>

      <button
        type="button"
        className="jp-btn jp-btn-amber jp-btn-block"
        onClick={onNext}
        disabled={!chosenId}
        data-testid="jp-commit-to-idea"
      >
        COMMIT TO THIS IDEA <ChevronRight size={14} />
      </button>
    </Parchment>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STAGE 4 — SADHANA (Practice) → CraftPrint blueprint
   ═══════════════════════════════════════════════════════════════ */
function Stage4Sadhana({ book, onInstall, installing, installError, alreadyInstalled }) {
  if (!book) return null;
  const cp = book.craftprint || {};
  return (
    <Parchment dataTestid="jp-stage-4">
      <p className="jp-stage-eyebrow">YOUR CRAFTPRINT™</p>
      <p className="jp-stage-lead">30-Day Execution Blueprint</p>

      <div className="jp-blueprint-head">
        <h3 className="jp-blueprint-title">{cp.idea_name}</h3>
        <p className="jp-blueprint-sub">CraftPrint™</p>
      </div>

      <ul className="jp-blueprint">
        <li><ScrollText size={15} /><div><span>SUTRA <em>(Principle)</em></span><p>{cp.sutra}</p></div></li>
        <li><Sun size={15} /><div><span>TRIGGER <em>(Nimitta)</em></span><p>{cp.trigger}</p></div></li>
        <li><Feather size={15} /><div><span>ACTION <em>(Kriya)</em></span><p>{cp.action}</p></div></li>
        <li><Calendar size={15} /><div><span>DURATION <em>(Kala)</em></span><p>{cp.duration_days} Days</p></div></li>
        <li><Target size={15} /><div><span>MEASURE <em>(Lakshya)</em></span><p>{cp.measure}</p></div></li>
        <li><Gift size={15} /><div><span>REWARD <em>(Phala)</em></span><p>{cp.reward}</p></div></li>
        <li><AlertTriangle size={15} /><div><span>OBSTACLE <em>(Vighna)</em></span><p>{cp.obstacle}</p></div></li>
        <li><Leaf size={15} /><div><span>ANTIDOTE <em>(Upaya)</em></span><p>{cp.antidote}</p></div></li>
      </ul>

      {installError ? <p className="jp-error" data-testid="jp-install-error">{installError}</p> : null}

      <button
        type="button"
        className="jp-btn jp-btn-amber jp-btn-block"
        onClick={onInstall}
        disabled={installing}
        data-testid="jp-start-journey"
      >
        {alreadyInstalled ? "VIEW MY 30-DAY JOURNEY" : installing ? "Sealing…" : "START MY 30-DAY JOURNEY"} <ChevronRight size={14} />
      </button>
    </Parchment>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STAGE 5 — SIDDHI (Achievement) → progress
   ═══════════════════════════════════════════════════════════════ */
function ProgressDial({ percent }) {
  const r = 58;
  const c = 2 * Math.PI * r;
  const safePct = Math.max(0, Math.min(100, Number(percent) || 0));
  const off = c - (safePct / 100) * c;
  return (
    <div className="jp-dial-wrap" data-testid="jp-dial">
      <svg viewBox="0 0 140 140" className="jp-dial" aria-hidden="true">
        <circle cx="70" cy="70" r={r} fill="none" stroke="#3A2F23" strokeWidth="8" />
        <circle
          cx="70" cy="70" r={r} fill="none"
          stroke="#DA9B4A" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off}
          transform="rotate(-90 70 70)"
        />
      </svg>
      <div className="jp-dial-center">
        <span className="jp-dial-pct">{Math.round(safePct)}%</span>
        <span className="jp-dial-lbl">COMPLETE</span>
      </div>
    </div>
  );
}

function Stage5Siddhi({ install, onReflect, onCheckin }) {
  // The week grid below is structured as 3 weeks × 7 days, populated from
  // checkin dates. Falls back to a pre-install showcase view.
  if (!install) {
    return (
      <Parchment dataTestid="jp-stage-5-empty">
        <p className="jp-stage-eyebrow">YOUR PROGRESS</p>
        <p className="jp-stage-lead">Transformation in Motion</p>
        <p className="jp-empty-line">Install your CraftPrint to unlock your progress chamber.</p>
      </Parchment>
    );
  }

  const days = install.duration_days || 30;
  const checkinSet = new Set((install.checkins || []).map((c) => c.date));
  // Render the most recent 21 days ending today, anchored so today is the
  // last cell. Today's check-in is always visible regardless of duration.
  const today = new Date();
  const gridRows = [];
  const start = new Date(today);
  start.setDate(today.getDate() - 20);
  for (let r = 0; r < 3; r++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      const idx = r * 7 + d;
      const dt = new Date(start);
      dt.setDate(start.getDate() + idx);
      const iso = dt.toISOString().slice(0, 10);
      row.push({ iso, done: checkinSet.has(iso), future: dt > today });
    }
    gridRows.push(row);
  }

  return (
    <Parchment dataTestid="jp-stage-5">
      <p className="jp-stage-eyebrow">YOUR PROGRESS</p>
      <p className="jp-stage-lead">Transformation in Motion</p>

      <div className="jp-progress-head">
        <ProgressDial percent={install.percent || 0} />
        <div className="jp-progress-stats">
          <div>
            <p className="jp-progress-val">{install.days_completed} / {install.duration_days}</p>
            <p className="jp-progress-lbl">Days Completed</p>
          </div>
          <div>
            <p className="jp-progress-val">
              <Flame size={18} className="jp-flame" /> {install.current_streak}
            </p>
            <p className="jp-progress-lbl">Current Streak</p>
          </div>
        </div>
      </div>

      <div className="jp-week-grid">
        <div className="jp-week-head">
          {["M","T","W","T","F","S","S"].map((d, i) => <span key={`weekday-${i}-${d}`}>{d}</span>)}
        </div>
        {gridRows.map((row, r) => (
          <div key={row[0]?.iso || `week-row-${r}`} className="jp-week-row">
            {row.map((cell) => (
              <span
                key={cell.iso}
                className={`jp-week-cell ${cell.done ? "is-done" : ""} ${cell.future ? "is-future" : ""}`}
                title={cell.iso}
              >
                {cell.done ? "✓" : ""}
              </span>
            ))}
          </div>
        ))}
      </div>

      <p className="jp-reflect-prompt" data-testid="jp-legacy-readonly">
        This is a legacy install, kept read-only. Your practice now lives in the new journey.
      </p>
      <Link
        to={`/bodha?book=${bookIdForSlug(install.book_slug) ?? install.book_slug}&showcase=${install.book_slug}`}
        className="jp-btn jp-btn-cream jp-btn-block"
        data-testid="jp-legacy-continue"
      >
        Continue in the new journey <ChevronRight size={14} />
      </Link>

      <div className="jp-insight-card">
        <p className="jp-insight-card-eyebrow"><Flag size={12} /> TRANSFORMATION INSIGHT</p>
        <p className="jp-insight-card-body">{install.craftprint?.transformation_insight}</p>
      </div>

      <div className="jp-milestone">
        <p className="jp-milestone-eyebrow"><Lock size={11} /> NEXT MILESTONE</p>
        <p className="jp-milestone-body">
          <strong>+ {install.duration_days} Days Completed</strong>
          <span>Identity Shift Installed</span>
        </p>
      </div>
    </Parchment>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════════════ */
export default function JourneyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isAuthenticated, authHeaders } = useAuth();

  const initialStage = Math.max(1, Math.min(5, parseInt(params.get("stage") || "1", 10) || 1));
  const [stage, setStage] = useState(initialStage);
  const [book, setBook] = useState(null);
  const [installs, setInstalls] = useState([]);
  const [chosenId, setChosenId] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [installError, setInstallError] = useState("");

  // Load showcase book
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/mycraftz/showcase/${slug}`)
      .then((res) => {
        if (!alive) return;
        setBook(res.data);
        setChosenId(res.data?.recommended_idea_id || null);
      })
      .catch(() => { if (alive) navigate("/", { replace: true }); });
    return () => { alive = false; };
  }, [slug, navigate]);

  // Load installs (auth only)
  const reloadInstalls = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await axios.get(`${API}/mycraftz/installed`, { headers: authHeaders() });
      setInstalls((res.data?.installed || []).filter((i) => i.book_slug === slug));
    } catch (err) {
    }
  };
  useEffect(() => { reloadInstalls(); /* eslint-disable-next-line */ }, [isAuthenticated, slug]);

  const myInstall = installs[0] || null;

  // Stage 4: install handler
  const handleInstall = async () => {
    // One system: continue into the real Bodha → Sankalpa → Sadhana journey.
    const target = `/bodha?book=${bookIdForSlug(slug) ?? slug}&showcase=${slug}`;
    if (!isAuthenticated) {
      navigate("/login", { state: { from: target } });
      return;
    }
    navigate(target);
  };

  // Stage 5: checkin
  const handleCheckin = async () => {
    if (!myInstall) return;
    try {
      await axios.post(`${API}/mycraftz/installed/${myInstall.id}/checkin`, {}, { headers: authHeaders() });
      await reloadInstalls();
    } catch (err) {
    }
  };

  // Stage 5: reflection — for now opens a prompt; can be upgraded later.
  const handleReflect = async () => {
    if (!myInstall) return;
    const text = window.prompt("Write your reflection (at least one sentence):");
    if (!text || text.trim().length < 4) return;
    try {
      await axios.post(`${API}/mycraftz/installed/${myInstall.id}/reflection`, { text: text.trim() }, { headers: authHeaders() });
      await reloadInstalls();
    } catch (err) {
    }
  };

  const goNext = () => setStage((s) => Math.min(5, s + 1));
  const goBack = () => {
    if (stage === 1) { navigate(-1); return; }
    setStage((s) => Math.max(1, s - 1));
  };

  if (!book) {
    return <div className="jp-page jp-loading"><p>Unrolling the scroll…</p></div>;
  }

  return (
    <div className="jp-page" data-testid="journey-page">
      <header className="jp-top">
        <Link to="/" className="jp-brand">
          <span className="jp-brand-name">iCRAFTZ<em>™</em></span>
          <span className="jp-brand-tag">Transform Knowledge. Build Legacy.</span>
        </Link>
        <div className="jp-top-title">
          <h1>THE TRANSFORMATION JOURNEY</h1>
          <p>From Insight to Identity</p>
        </div>
        <div className="jp-top-spacer" />
      </header>

      <StageIndicator stage={stage} onJump={(n) => setStage(n)} />

      <main className="jp-main">
        <AnimatePresence mode="wait">
          <motion.div
            key={stage}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="jp-stage-wrap"
          >
            {stage === 1 && <Stage1Jigyasa book={book} onNext={goNext} />}
            {stage === 2 && <Stage2Bodha book={book} onNext={goNext} />}
            {stage === 3 && <Stage3Sankalpa book={book} chosenId={chosenId} setChosenId={setChosenId} onNext={goNext} />}
            {stage === 4 && (
              <Stage4Sadhana
                book={book}
                onInstall={handleInstall}
                installing={installing}
                installError={installError}
                alreadyInstalled={!!myInstall}
              />
            )}
            {stage === 5 && (
              <Stage5Siddhi install={myInstall} onReflect={handleReflect} onCheckin={handleCheckin} />
            )}
          </motion.div>
        </AnimatePresence>

        <div className="jp-nav">
          <button type="button" className="jp-nav-btn" onClick={goBack} data-testid="jp-back">
            <ArrowLeft size={14} /> Back
          </button>
          {stage < 5 ? (
            <button type="button" className="jp-nav-btn" onClick={goNext} data-testid="jp-next">
              Next <ArrowRight size={14} />
            </button>
          ) : (
            <Link to="/mycraftz" className="jp-nav-btn" data-testid="jp-view-vault">
              View Vault <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </main>

      <JourneyFooter />
    </div>
  );
}
